/**
 * Distance-faded labels for every non-hub, non-center position/move node,
 * capped to the MAX_VISIBLE labels closest to the camera per frame. Capping
 * was added because even with distance-fade, 5-10 simultaneous labels at
 * close zoom got visually cluttered. The closest few are nearly always the
 * ones the user cares about.
 *
 * Implementation: one parent component owns all label DOM refs in a Map.
 * Each frame:
 *   1. compute camera-distance + forward-cone check for every candidate.
 *   2. select the MAX_VISIBLE smallest-distance candidates that pass the
 *      cone check. Everyone else: opacity 0, pointer-events off.
 *   3. For each visible candidate, set opacity from the distance-fade
 *      curve, multiplied by DIM_FACTOR if the node is outside the active
 *      glow set.
 *
 * All opacity writes go straight to refs (no React re-renders).
 */
import { useEffect, useMemo, useRef } from 'react';
import { Html } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { ClusterBy, Graph, GraphNode, Move, Position } from '../types/domain';
import type { ForceLayout } from './useForceLayout';
import { useAppStore } from '../state/store';
import { FOCUS_DIM, dimmedValue } from '../zones/palette';
import { dragState } from '../state/drag';
import { labelAnchorOffset } from './sizing';
import { boostOf } from './interactBoost';

// Camera distances at which a label fades in/out, *for a typical-weight
// node*. Low-weight (small) nodes get more generous values so labels
// surface earlier when scanning sparse outer territory.
const NEAR_DIST_BASE = 11;
const FAR_DIST_BASE = 42;
const FORWARD_DOT_THRESHOLD = 0.2;
const MAX_VISIBLE = 5;
// Text, not geometry — so this is an opacity multiplier and not the same
// unit as the sphere's colour dim, even though the two tuned values
// landed close. 0.06 is where it was tuned at TUNED_FOCUS_DIM; the
// multiplier actually applied is carried from there to whatever
// FOCUS_DIM currently is, so labels and spheres recede together. Folded
// once at module load. See palette.ts.
const DIM_FACTOR_TUNED = 0.06;
const DIM_FACTOR = dimmedValue(DIM_FACTOR_TUNED, FOCUS_DIM);

const TMP_VEC = new THREE.Vector3();
const TMP_FWD = new THREE.Vector3();
const TMP_UP = new THREE.Vector3();


type Props = {
  graph: Graph;
  layout: ForceLayout;
  excludeIds: Set<string>;
  glowNodes: Set<string>;
  clusterBy: ClusterBy;
  positionsById: Map<string, Position>;
  movesById: Map<string, Move>;
};

// Single-glyph indicator for a position's type — always-on accent so the
// "where am I in the zone hierarchy" reading stays visible even when
// clustering is by something other than positionType. Suppressed when
// clusterBy === 'positionType' (color already conveys this).
const POSITION_TYPE_SYMBOL: Record<string, string> = {
  top: '↑',
  bottom: '↓',
  neutral: '↔',
  flux: '⟳',
  terminal: '⊥',
};
function positionTypeSymbol(p: Position): string | null {
  if (p.tags?.includes('symmetric')) return POSITION_TYPE_SYMBOL.neutral;
  const z = p.zone;
  if (z === 'submitted') return POSITION_TYPE_SYMBOL.terminal;
  if (z === 'scrambled') return POSITION_TYPE_SYMBOL.flux;
  if (z === 'standing' || z === 'clinch') return POSITION_TYPE_SYMBOL.neutral;
  if (z.endsWith('-top')) return POSITION_TYPE_SYMBOL.top;
  if (z.endsWith('-bottom')) return POSITION_TYPE_SYMBOL.bottom;
  return null;
}

export function ProximityLabels({
  graph,
  layout,
  excludeIds,
  glowNodes,
  clusterBy,
  positionsById,
  movesById,
}: Props) {
  // movesById is part of the Props contract for parity with the
  // other label components, but no cluster code in this file
  // currently dereferences it — kept here so the prop signature
  // stays uniform across HubLabels / ProximityLabels / ClusterLabels.
  void movesById;
  // Interception nodes are now labelled too — same proximity treatment
  // as positions and moves. They share the warm accent color via the
  // ZoneBadge / cluster code so the label distinguishes them visually
  // even at distance.
  const candidates = graph.nodes.filter(
    (n) =>
      (n.kind === 'position' || n.kind === 'move' || n.kind === 'interception') &&
      !excludeIds.has(n.id),
  );

  // Always-on position-type symbol for POSITION nodes. Suppressed when
  // clusterBy === 'positionType' (the cluster palette already conveys
  // the same info) so we don't double-paint top/bottom.
  const typeSymbol = useMemo(() => {
    const map = new Map<string, string>();
    if (clusterBy === 'positionType') return map;
    for (const n of candidates) {
      if (n.kind !== 'position') continue;
      const p = positionsById.get(n.id);
      if (!p) continue;
      const sym = positionTypeSymbol(p);
      if (sym) map.set(n.id, sym);
    }
    return map;
  }, [candidates, clusterBy, positionsById]);

  const labelRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const groupRefs = useRef<Map<string, THREE.Group>>(new Map());
  const innerGroupRefs = useRef<Map<string, THREE.Group>>(new Map());
  const distScratch = useRef<
    { node: GraphNode; d: number; forwardOk: boolean; near: number; far: number }[]
  >([]);

  // Selected node id, read via a ref so per-frame label writes don't
  // pay a React re-render. Mirrors Nodes.tsx's subscription.
  const selectedIdRef = useRef<string | null>(null);
  const hoverIdRef = useRef<string | null>(null);
  useEffect(
    () =>
      useAppStore.subscribe((st) => {
        hoverIdRef.current = st.hoverId;
        const sel = st.selection;
        selectedIdRef.current =
          sel.kind === 'position'
            ? sel.id
            : sel.kind === 'move'
              ? sel.id
              : sel.kind === 'counter'
                ? `intercept:${sel.moveId}:${sel.id}`
                : null;
      }),
    [],
  );

  useFrame(({ camera }) => {
    if (!layout.posRef.current) return;
    const pos = layout.posRef.current;
    camera.getWorldDirection(TMP_FWD);
    // Camera's local +Y in world space — the screen "up" direction.
    TMP_UP.set(0, 1, 0).applyQuaternion(camera.quaternion);

    // --- Pass 1: compute distances + position groups ---
    const arr = distScratch.current;
    arr.length = 0;
    for (const n of candidates) {
      const idx = layout.nodeIndex.get(n.id);
      if (idx === undefined) continue;
      const x = pos[idx * 3 + 0];
      const y = pos[idx * 3 + 1];
      const z = pos[idx * 3 + 2];

      const g = groupRefs.current.get(n.id);
      if (g) g.position.set(x, y, z);
      // Inner group lives in node-local space; setting its position to
      // cameraUp * offset puts the Html anchor just above the bridge ring
      // in screen space, at the correct world distance for any zoom.
      const inner = innerGroupRefs.current.get(n.id);
      if (inner) {
        // Sized off the node's ACTUAL drawn radius (degree-driven, plus
        // the selection/hover inflation) rather than a weight-based
        // guess — otherwise big or selected spheres grow over the label.
        const off = labelAnchorOffset(n, {
          // Animated, so the label glides with the sphere
          // instead of jumping when selection changes.
          boost: boostOf(n.id),
        });
        inner.position.set(TMP_UP.x * off, TMP_UP.y * off, TMP_UP.z * off);
      }

      const dx = camera.position.x - x;
      const dy = camera.position.y - y;
      const dz = camera.position.z - z;
      const d = Math.sqrt(dx * dx + dy * dy + dz * dz);

      TMP_VEC.set(x - camera.position.x, y - camera.position.y, z - camera.position.z);
      const len = TMP_VEC.length() || 1;
      const forwardDot = TMP_VEC.dot(TMP_FWD) / len;
      const forwardOk = forwardDot >= FORWARD_DOT_THRESHOLD;

      // Smaller / less-connected nodes get a more generous distance band
      // so their labels appear sooner when the camera approaches. weight
      // is in [0,1]; map to a 0.6x..1.0x multiplier where 0 → 0.6x (more
      // generous) and 1 → 1.0x (typical).
      const weightScale = 0.6 + 0.4 * (n.weight ?? 0.5);
      // Inverse: small nodes -> larger near/far. Multiply by 1/scale.
      const inv = 1 / Math.max(0.3, weightScale);
      const near = NEAR_DIST_BASE * inv;
      const far = FAR_DIST_BASE * inv;

      arr.push({ node: n, d, forwardOk, near, far });
    }

    // --- Pass 2: pick MAX_VISIBLE nearest (that pass the cone check) ---
    // Active-set members ALWAYS get a label, regardless of distance or
    // proximity cap — when a node is selected we want to see exactly
    // what's connected to it.
    const eligible = arr.filter(
      (e) => e.forwardOk && e.d < e.far && !glowNodes.has(e.node.id),
    );
    eligible.sort((a, b) => a.d - b.d);
    const visibleIds = new Set<string>();
    for (let i = 0; i < Math.min(MAX_VISIBLE, eligible.length); i++) {
      visibleIds.add(eligible[i].node.id);
    }
    for (const e of arr) {
      if (glowNodes.has(e.node.id)) visibleIds.add(e.node.id);
    }

    // --- Pass 3: write opacity to every candidate label ---
    for (const e of arr) {
      const labelEl = labelRefs.current.get(e.node.id);
      if (!labelEl) continue;

      if (!visibleIds.has(e.node.id)) {
        labelEl.style.opacity = '0';
        labelEl.style.pointerEvents = 'none';
        continue;
      }

      // Active-set members render at full opacity unconditionally.
      const inActiveSet = glowNodes.has(e.node.id);
      const isSelected = selectedIdRef.current === e.node.id;
      let opacity: number;
      if (inActiveSet) {
        opacity = 1;
      } else {
        opacity = (e.far - e.d) / (e.far - e.near);
        if (opacity < 0) opacity = 0;
        if (opacity > 1) opacity = 1;
        if (glowNodes.size > 0) {
          opacity *= dimmedValue(DIM_FACTOR, FOCUS_DIM);
        }
      }
      labelEl.style.opacity = opacity.toFixed(3);
      labelEl.style.pointerEvents = opacity > 0.1 ? 'auto' : 'none';
      // Third selection cue: the selected node's own label goes bright
      // and full-weight while everything else stays quiet. Cheapest and
      // most literal signal — you read the name instead of decoding a
      // shape — and it survives zoom, angle and node size.
      labelEl.style.color = isSelected ? '#ffffff' : '';
      labelEl.style.fontWeight = isSelected ? '700' : '';
      labelEl.style.textShadow = isSelected
        ? '0 0 10px rgba(223,240,247,0.55)'
        : '';
    }
  });

  return (
    <>
      {candidates.map((n) => (
        <Label
          key={n.id}
          node={n}
          groupRefs={groupRefs}
          innerGroupRefs={innerGroupRefs}
          labelRefs={labelRefs}
          typeSymbol={typeSymbol.get(n.id)}
        />
      ))}
    </>
  );
}

function Label({
  node,
  groupRefs,
  innerGroupRefs,
  labelRefs,
  typeSymbol,
}: {
  node: GraphNode;
  groupRefs: React.MutableRefObject<Map<string, THREE.Group>>;
  innerGroupRefs: React.MutableRefObject<Map<string, THREE.Group>>;
  labelRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  typeSymbol?: string;
}) {
  const setHover = useAppStore((s) => s.setHover);
  const setSelection = useAppStore((s) => s.setSelection);
  const cancelClearHover = useAppStore((s) => s.cancelClearHover);
  const clearHoverSoon = useAppStore((s) => s.clearHoverSoon);

  return (
    <group
      ref={(el) => {
        if (el) groupRefs.current.set(node.id, el);
        else groupRefs.current.delete(node.id);
      }}
    >
      {/* Inner group is positioned each frame by the parent's useFrame at
          cameraScreenUp * worldOffset. Html projects to a screen point
          that sits just above the node's outer ring at any zoom level. */}
      <group
        ref={(el) => {
          if (el) innerGroupRefs.current.set(node.id, el);
          else innerGroupRefs.current.delete(node.id);
        }}
      >
        <Html
          position={[0, 0, 0]}
          zIndexRange={[70, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div
            ref={(el) => {
              if (el) labelRefs.current.set(node.id, el);
              else labelRefs.current.delete(node.id);
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (node.kind === 'position') setSelection({ kind: 'position', id: node.id });
              else if (node.kind === 'move') setSelection({ kind: 'move', id: node.id });
              else if (node.kind === 'interception' && node.parentMoveId) {
                // Interception node id is `intercept:<moveId>:<counterId>` —
                // pull the counter id out of the parent-move association
                // and dispatch a counter selection.
                const parts = node.id.split(':');
                if (parts.length === 3)
                  setSelection({ kind: 'counter', id: parts[2], moveId: node.parentMoveId });
              }
            }}
            onPointerEnter={() => {
              // Honor the global press gate so a click-drag pan
              // sweeping the cursor across a label doesn't flash a
              // hover state on its underlying node.
              if (dragState.isPressed) return;
              cancelClearHover();
              setHover(node.id);
            }}
            onPointerLeave={clearHoverSoon}
            style={{
              // Center horizontally on anchor, lift bottom edge to anchor,
              // plus a tiny 4px CSS gap so the text never quite touches the
              // ring at the closest zoom.
              transform: 'translate(-50%, calc(-100% - 4px))',
              color: '#cad3df',
              fontSize: 10,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              fontWeight: 300,
              opacity: 0,
              textShadow: '0 0 6px rgba(0,0,0,0.85)',
              whiteSpace: 'nowrap',
              cursor: 'pointer',
              userSelect: 'none',
              textAlign: 'center',
              transition: 'opacity 120ms ease',
            }}
          >
            {node.label}
            {typeSymbol ? (
              <span
                style={{
                  opacity: 0.7,
                  marginLeft: 5,
                  // Slightly larger and bolder than the cluster suffix
                  // because the symbol is a single glyph carrying the
                  // semantic load. No letter-spacing so the arrow renders
                  // cleanly.
                  fontSize: 11,
                  fontWeight: 400,
                  letterSpacing: 'normal',
                  color: '#e6e8ef',
                }}
                aria-label="position type"
              >
                {typeSymbol}
              </span>
            ) : null}
          </div>
        </Html>
      </group>
    </group>
  );
}
